import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import AuthHeader from '../features/auth/AuthHeader'
import LegalLink from '../components/LegalLink'
import signupPanel from '../assets/register/img-signup.svg'
import EyeIcon from '../components/EyeIcon'
import { ReactComponent as IconRequirements } from '../assets/register/icon-req.svg'
import { useAuth } from '../features/auth/AuthContext'

const TooltipStatusIcon = ({ valid, color }) => (
  <svg width="8" height="8" viewBox="0 0 8 8" fill="none" stroke={color} strokeLinecap="round">
    {valid ? (
      <path d="M1 3.73851L3.0928 5.96206C3.21486 6.0186 3.38514 5.94402 3.42 5.86L7 2" />
    ) : (
      <path d="M1.5 1.5L6.5 6.5M6.5 1.5L1.5 6.5" />
    )}
  </svg>
)

// M3.5.3: the fields had `outline-none` and nothing to replace it, so focus was invisible.
// The focus border matches the checkout field focus (#4CBC18, D4.2.3) — but only while the
// field is neither in error nor already valid, so focus never masks a validation state.
const inputClass = (error, success) => {
  const border = error
    ? 'border-[#d85263]'
    : success
      ? 'border-[#4cbc18]'
      : 'border-[#cbdcc3] focus:border-[#4cbc18]'
  return `w-full h-[48px] bg-white border ${border} rounded-full px-6 text-[14px] text-brand-dark placeholder:text-[#909090] outline-none transition-colors`
}

const Signup = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const nextPath = searchParams.get('next')
  const { signup } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [emailTouched, setEmailTouched] = useState(false)
  const [passwordTouched, setPasswordTouched] = useState(false)
  const [showRequirements, setShowRequirements] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  const passwordMeetsLength = password.length >= 8
  const passwordTooShort = password.length > 0 && !passwordMeetsLength

  const showEmailError = emailTouched && email.length > 0 && !isEmailValid
  const showEmailSuccess = emailTouched && isEmailValid
  const showPasswordError = passwordTouched && passwordTooShort
  const showPasswordSuccess = passwordTouched && passwordMeetsLength

  const handleSubmit = async (e) => {
    e.preventDefault()
    setEmailTouched(true)
    setPasswordTouched(true)
    if (!isEmailValid || !passwordMeetsLength) return
    setError(null)
    setSubmitting(true)
    try {
      const session = await signup({ email, password })
      if (session?.user && session?.accessToken) {
        const dest = nextPath && nextPath.startsWith('/') ? nextPath : '/pipeline'
        navigate(dest, { replace: true })
      }
    } catch (err) {
      setError(err.message || 'Unable to create account')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-brand-pageFill flex flex-col">
      {/* M3.5.1: the #DAE4D5 bottom stroke was missing here only — now shared.
          Review #3, D1.4: the bar used to sit inside the right-hand column, so its logo was
          offset by the whole marketing panel — 1400px on a 2560 viewport, dead centre, and 828px
          at 1280. Every other auth page puts the logo at the page's left gutter, and Микола asked
          in set #2 for the auth bars to be consistent, so the bar spans the page here too and the
          panel starts under it. No frame above 1440 exists, so the wide-screen behaviour was ours
          to choose; this is the reading that makes the five auth pages agree. */}
      <AuthHeader />

      <div className="flex-1 flex">
        {/* The panel was a fixed 708px while the form column was flex-1, so the split only looked
            like the design's half-and-half at 1440. At 1280 the panel took 55% of the screen.
            Proportional holds the design's ratio at any width (708/1440 = 49.2%). */}
        <aside className="hidden lg:block lg:w-1/2 shrink-0 bg-brand-dark">
          <img
            src={signupPanel}
            alt="Start analyzing deals with clarity and confidence"
            className="w-full h-full object-cover"
          />
        </aside>

        {/* M3.3: 40px from the navbar to the content, all breakpoints. */}
        <main className="flex-1 flex items-start justify-center px-6 pt-[40px]">
          <form onSubmit={handleSubmit} className="w-full max-w-[416px] flex flex-col pb-6">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-brand-primary text-[16px] font-semibold hover:text-brand-hover self-start"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M13 5l-5 5 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back
            </button>

            {/* Set #4.3.3: back button to heading is 16px on desktop, 8px on mobile — the two
                breakpoints disagree, so this pair can no longer ride the form's uniform gap. */}
            <div className="mt-2 md:mt-4 flex flex-col gap-4">
              <h1 className="text-[28px] leading-[36px] md:text-[40px] md:leading-[48px] font-extrabold text-brand-dark">
                Start Free Trial
              </h1>
              {/* Set #4 mobile: reverting the previous request — stacked one per row below md,
                  not wrap-to-fit, so row membership never depends on how much of the three labels
                  happens to fit. gap-3 (12px) is a chosen value, not a spec number: client asked for
                  "very slightly increase" over the prior 8px stacked gap with no px given. */}
              <div className="flex flex-col items-start gap-3 md:flex-row md:flex-nowrap md:items-center md:gap-x-3 md:gap-y-0">
                {['7 days', 'Analyze up to 5 properties', 'No credit card required'].map((t) => (
                  <div key={t} className="flex items-center gap-1.5 shrink-0">
                    <span className="w-[20px] h-[20px] rounded-full bg-brand-primary flex items-center justify-center shrink-0">
                      <svg width="10" height="8" viewBox="0 0 12 9" fill="none">
                        <path d="M1 4.5L4.5 8L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <span className="text-[12px] text-brand-dark whitespace-nowrap">{t}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Gap to the checkmark row above is unchanged by Set #4 — keeping the prior 24/32. */}
            <div className="mt-6 md:mt-8 flex flex-col gap-4">
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setEmailTouched(true)}
                  placeholder="E-mail"
                  autoComplete="email"
                  className={inputClass(showEmailError, showEmailSuccess)}
                />
                {showEmailError && (
                  <p className="mt-2 pl-6 text-[12px] text-[#d85263]">
                    Please enter a valid email address.
                  </p>
                )}
              </div>

              <div className="relative">
                {/* M3.5.6: the show/hide button used to be centred against this whole
                    block, so it slid down as soon as the validation line appeared below
                    the field. It is now centred against the input alone. */}
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setShowRequirements(true)}
                    onBlur={() => {
                      setPasswordTouched(true)
                      setShowRequirements(false)
                    }}
                    placeholder="Password"
                    autoComplete="new-password"
                    className={`${inputClass(showPasswordError, showPasswordSuccess)} pr-16`}
                  />
                  {/* D3.3.2-new: the same icon the login page uses, which he prefers. It was
                      a pair of exported .svg files here and an inline drawing there. */}
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-5 top-1/2 -translate-y-1/2"
                    tabIndex={-1}
                  >
                    <EyeIcon open={showPassword} />
                  </button>
                </div>
                {showRequirements && (
                  <div className="absolute right-0 -bottom-2 translate-y-full z-10 bg-white border border-[#cbdcc3] rounded-md p-3 shadow-md flex items-center gap-2 text-[12px]">
                    <TooltipStatusIcon
                      valid={passwordMeetsLength}
                      color={passwordMeetsLength ? '#4cbc18' : '#d85263'}
                    />
                    <span className={passwordMeetsLength ? 'text-brand-dark' : 'text-[#d85263]'}>
                      Use at least 8 characters.
                    </span>
                  </div>
                )}
                {showPasswordError && !showRequirements && (
                  <p className="mt-2 pl-6 text-[12px] text-[#d85263]">
                    Password does not meet criteria.
                  </p>
                )}
              </div>
            </div>

            {error && <p className="mt-6 md:mt-8 text-[13px] text-[#d85263]">{error}</p>}

            {/* Set #4.3.1: 24px from the Password field, both breakpoints — no md override. */}
            <p className="mt-6 text-[10px] leading-[16px] text-brand-dark text-center">
              By signing up, you agree to the{' '}
              <LegalLink to="/terms" className="font-bold hover:text-brand-hover transition-colors">Terms</LegalLink> and{' '}
              <LegalLink to="/privacy" className="font-bold hover:text-brand-hover transition-colors">Privacy Policy</LegalLink> of Arvance,&nbsp;LLC
            </p>

            {/* Set #4.3.1: 16px from the disclaimer, both breakpoints. */}
            <button
              type="submit"
              disabled={submitting}
              /* Figma `signup_step1`: 240×56 centred on desktop; full width on mobile. */
              className="mt-4 w-full md:w-[240px] md:mx-auto h-[56px] rounded-full bg-brand-primary hover:bg-brand-hover text-white text-[16px] font-bold disabled:opacity-60 flex items-center justify-center gap-2 transition-colors"
            >
              {submitting ? 'Creating account…' : (<>Create Account<svg width="17" height="12" viewBox="0 0 17 12" fill="none"><path d="M11 1l5 5-5 5M16 6H1" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></>)}
            </button>

            {/* Set #4.3.2: 12px from the Create Account CTA, both breakpoints. */}
            <p className="mt-3 text-center text-[14px] text-brand-dark">
              Already have an account?{' '}
              <Link to="/login" className="font-semibold text-brand-green hover:text-brand-hover transition-colors">Sign in</Link>
            </p>
          </form>
        </main>
      </div>
      <IconRequirements className="hidden" />
    </div>
  )
}

export default Signup
